import { type GroupRoleState, useGroupRole } from '@jbroll/rowboat-sharing-react';
import { useState } from 'react';
import { useActiveEstate } from '@/hooks/useActiveEstate';
import { ANON_IDENTITY, signOut, usePort, useRowboat, useSelect } from '@/rowboat';
import { setCaptureContext } from '@/services/captureContext';
import { archiveEstate, createEstate } from '@/services/estateService';
import {
  archiveItemGroup,
  createItemGroup,
  defaultPrefix,
  listItemGroups,
  prefixProblem,
  renameItemGroup,
} from '@/services/itemGroupService';
import { itemsByEstate, itemsByItemGroup } from '@/services/itemService';
import { AddForm, EditableList, IconButton, type ListEntry } from './EditableList';
import { ExportPanel } from './ExportPanel';
import { ShareDrawer } from './ShareDrawer';
import { Alert, Button, Drawer, Muted, ShareIcon, TextInput, TrashIcon } from './ui';

const sameEntries = (a: ListEntry[], b: ListEntry[]) =>
  a.length === b.length &&
  a.every(
    (e, i) =>
      e.id === b[i].id &&
      e.name === b[i].name &&
      e.detail === b[i].detail &&
      e.itemCount === b[i].itemCount,
  );

/**
 * The header menu: the active project and its groups, export and sharing, and sign-out.
 * `onSwitch` runs once a different project is active, so the caller can close the menu.
 */
export function ProjectMenu({ onClose, onSwitch }: { onClose: () => void; onSwitch: () => void }) {
  const graph = useRowboat();
  const { author, mintGroup } = usePort();
  // Anonymous users act as ANON_IDENTITY (never ""): their rows live in the local anon
  // store and are adopted into the account on signup.
  const actor = author ?? ANON_IDENTITY;
  const { estates, activeEstate } = useActiveEstate();
  const estateId = activeEstate?.id ?? '';
  const groups = useSelect(
    () =>
      listItemGroups(graph, estateId).map((r) => ({
        id: r.id,
        name: r.name,
        detail: r.prefix,
        itemCount: itemsByItemGroup(graph, r.id).length,
      })),
    sameEntries,
  );

  const [addingProject, setAddingProject] = useState(false);
  const [error, setError] = useState('');

  const fail = (e: unknown) => setError(e instanceof Error ? e.message : 'Something went wrong');

  const selectEstate = (id: string) => {
    if (!id) return;
    void setCaptureContext(graph, actor, { estateId: id }).then(onSwitch, fail);
  };

  const createProject = async (name: string) => {
    const groupId = await mintGroup(crypto.randomUUID());
    const id = await createEstate(graph, { name, author: actor, groupId });
    await setCaptureContext(graph, actor, { estateId: id });
    onSwitch();
  };

  const deleteProject = async () => {
    if (!activeEstate) return;
    const n = itemsByEstate(graph, activeEstate.id).length;
    const items = n === 0 ? '' : ` and its ${n} ${n === 1 ? 'item' : 'items'}`;
    if (!window.confirm(`Delete ${activeEstate.name}${items}?`)) return;
    const next = estates.find((e) => e.id !== activeEstate.id)?.id ?? '';
    try {
      await archiveEstate(graph, activeEstate.id);
      await setCaptureContext(graph, actor, { estateId: next });
      onSwitch();
    } catch (e) {
      fail(e);
    }
  };

  const owner = activeEstate?.owner_group_id ?? '';
  // Anonymous projects never sync, and a project in the root group belongs to this account
  // alone, so neither has a membership to ask about.
  const inRoot = !!author && owner === author;
  const roleState = useGroupRole(author && owner && !inRoot ? owner : null);
  const role = roleState.status === 'ok' ? roleState.role : null;
  // Archiving hides a project from every member, so only its admins may delete it.
  const canDelete = !author || inRoot || role === 'admin';
  // The root group holds every private row of the account; the server refuses to share it.
  const canShare = !!author && !inRoot && role === 'admin';
  const readOnly = role === 'reader';
  const [sharing, setSharing] = useState(false);

  return (
    <Drawer title="Project" onClose={onClose}>
      <div className="space-y-4">
        <div>
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-content-secondary">Active project</span>
            <IconButton label="New project" onClick={() => setAddingProject(true)}>
              <span className="text-xl leading-none">+</span>
            </IconButton>
          </div>
          <div className="flex items-center gap-2">
            <select
              aria-label="Select project"
              value={estateId}
              onChange={(e) => selectEstate(e.target.value)}
              className="min-h-11 w-full min-w-0 rounded-xl border border-divider bg-surface-primary px-3 py-2.5 text-base text-content-primary"
            >
              {!activeEstate && (
                <option value="" disabled>
                  Select a project…
                </option>
              )}
              {estates.map((e) => (
                <option key={e.id} value={e.id}>
                  {e.name}
                </option>
              ))}
            </select>
            {activeEstate && canShare && (
              <IconButton
                label={`Share project ${activeEstate.name}`}
                onClick={() => setSharing(true)}
              >
                <ShareIcon />
              </IconButton>
            )}
            {activeEstate && canDelete && (
              <IconButton
                label={`Delete project ${activeEstate.name}`}
                onClick={() => void deleteProject()}
              >
                <TrashIcon />
              </IconButton>
            )}
          </div>
          {activeEstate && <RoleStatus state={roleState} />}
          {addingProject && (
            <NewProjectForm onCreate={createProject} done={() => setAddingProject(false)} />
          )}
        </div>
        {sharing && activeEstate && author && canShare && (
          <ShareDrawer
            groupId={owner}
            projectName={activeEstate.name}
            selfId={author}
            onClose={() => setSharing(false)}
          />
        )}
        {activeEstate && (
          <>
            <EditableList
              title="Groups"
              noun="group"
              entries={groups}
              readOnly={readOnly}
              emptyText={
                readOnly
                  ? 'No groups yet.'
                  : "No groups yet. Add one with +. Its prefix starts each item number and can't change later."
              }
              renderAdd={(done) => (
                <NewGroupForm
                  onCreate={(name, prefix) =>
                    createItemGroup(graph, {
                      estateId,
                      name,
                      prefix,
                      groupId: owner,
                      author: actor,
                    })
                  }
                  problem={(prefix) => prefixProblem(graph, estateId, prefix)}
                  done={done}
                />
              )}
              onRename={(id, name) => renameItemGroup(graph, id, name)}
              onDelete={(id) => archiveItemGroup(graph, id)}
              onError={fail}
            />
            <ExportPanel estateId={activeEstate.id} estateName={activeEstate.name} />
          </>
        )}
        {author && (
          <Button variant="secondary" onClick={() => void signOut()}>
            Sign out
          </Button>
        )}
        {error && <Alert>{error}</Alert>}
      </div>
    </Drawer>
  );
}

/** Why Share and Delete are missing while the role is unknown. */
function RoleStatus({ state }: { state: GroupRoleState }) {
  switch (state.status) {
    case 'idle':
    case 'ok':
      return null;
    case 'loading':
      return <Muted>Checking your access to this project…</Muted>;
    case 'error':
      return (
        <Alert>
          Could not check your access to this project, so Share and Delete are hidden. It checks
          again when you come back to this tab or reconnect.
        </Alert>
      );
  }
}

function NewProjectForm({
  onCreate,
  done,
}: {
  onCreate: (name: string) => Promise<unknown>;
  done: () => void;
}) {
  const [name, setName] = useState('');
  return (
    <AddForm
      noun="project"
      ready={name.trim() !== ''}
      onCreate={() => onCreate(name.trim())}
      done={done}
    >
      {(field) => (
        <TextInput
          autoFocus
          aria-label="New project name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="New project name"
          {...field}
        />
      )}
    </AddForm>
  );
}

/** Name and prefix together: the prefix is fixed once the group exists. */
function NewGroupForm({
  onCreate,
  problem,
  done,
}: {
  onCreate: (name: string, prefix: string) => Promise<unknown>;
  problem: (prefix: string) => string;
  done: () => void;
}) {
  const [name, setName] = useState('');
  // Follows the name until the user types a prefix of their own.
  const [typedPrefix, setTypedPrefix] = useState<string | null>(null);
  const prefix = typedPrefix ?? (name.trim() ? defaultPrefix(name) : '');
  const issue = name.trim() || typedPrefix !== null ? problem(prefix) : '';

  return (
    <AddForm
      noun="group"
      ready={name.trim() !== '' && issue === ''}
      issue={issue}
      onCreate={() => onCreate(name.trim(), prefix)}
      done={done}
    >
      {(field) => (
        <>
          <TextInput
            autoFocus
            aria-label="New group name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="New group"
            {...field}
          />
          <div className="w-24 shrink-0">
            <TextInput
              aria-label="New group prefix"
              value={prefix}
              onChange={(e) => setTypedPrefix(e.target.value.toUpperCase())}
              placeholder="Prefix"
              maxLength={4}
              autoComplete="off"
              {...field}
            />
          </div>
        </>
      )}
    </AddForm>
  );
}
